<script setup lang="ts">
import { type SelectionItem } from '@models/selectionItem.ts';
import Divider from '@components/Divider.vue';
import SelectionItemActions from '@organisms/offer/selection-items/components/SelectionItemActions.vue';
import SelectionItemContent from '@organisms/offer/selection-items/components/SelectionItemContent.vue';
import Typography from '@components/Typography.vue';
import { getCompleteShorterReadableDate } from '@tools/date';
import { computed } from 'vue';
import type { AccountManager } from '@models/accountManager.ts';
import { transchoice } from '@elements/translation';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { storeToRefs } from 'pinia';

const props = defineProps<{ selectionItem: SelectionItem }>();

const accountManager = computed<AccountManager | null>(() => props.selectionItem.pipedrive_user);

const companyName = computed<string>(() => window.COMPANY_NAME);

const selectionItemStore = useSelectionItemStore();
const { setHoverSelectionItemId } = selectionItemStore;
const { hoverSelectionItemId } = storeToRefs(selectionItemStore);
</script>

<template>
  <div
    class="selection-item-element"
    :class="{ 'selection-item-element--hover-highlighted': selectionItem.id === hoverSelectionItemId }"
  >
    <div
      class="selection-item-element__content"
      @mouseenter="setHoverSelectionItemId(selectionItem.id)"
      @mouseleave="setHoverSelectionItemId(null)"
    >
      <SelectionItemContent :selection-item="selectionItem" />

      <Divider class="m-0" />

      <SelectionItemActions :selection-item="selectionItem" />
    </div>

    <Typography
      variant="caption"
      color="gray-darker"
    >
      <template v-if="selectionItem.proposal && accountManager">
        {{ transchoice(
          'selection_item.timestamp.account_manager.selected_by',
          {
            name: accountManager.firstname,
            company_name: companyName,
            date: getCompleteShorterReadableDate(selectionItem.created_at.iso),
          }
        )
        }}
      </template>

      <template v-else>
        {{ transchoice(
          'selection_item.timestamp.selected_by',
          {
            name: selectionItem.user.firstname,
            date: getCompleteShorterReadableDate(selectionItem.created_at.iso),
          }
        )
        }}
      </template>
    </Typography>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-element {
  display: flex;
  flex-direction: column;
  gap: rem(4);

  &--hover-highlighted &__content {
    box-shadow: 0 rem(4) rem(8) 0 rgba(0, 0, 0, 0.25);
  }

  &--scroll-highlighted &__content {
    box-shadow: 0 rem(4) rem(16) 0 rgba(0, 0, 0, 0.25);
  }

  &--dragged-highlighted &__content {
    border-color: $colorBlueLighter;
    border-radius: rem(8);
    background-color: $colorBlueLightest;
  }

  &__content {
    border-radius: rem(8);
    border: 1px solid $colorGray;
  }
}
</style>
